import { useEffect, useRef, useState } from "react";

/**
 * CountUp — animates from 0 to a numeric target when the element enters viewport.
 * Preserves any non-numeric prefix/suffix in the original string (e.g. "+340h", "12+", "100%").
 */
function parseTarget(raw) {
  const str = String(raw);
  const match = str.match(/(-?\d+(?:\.\d+)?)/);
  if (!match) return { prefix: str, suffix: "", target: 0, hasNumber: false };
  const numStr = match[1];
  const idx = str.indexOf(numStr);
  return {
    prefix: str.slice(0, idx),
    suffix: str.slice(idx + numStr.length),
    target: Number(numStr),
    hasNumber: true,
    decimals: (numStr.split(".")[1] || "").length,
  };
}

export default function CountUp({ value, duration = 1500, className = "" }) {
  const elRef = useRef(null);
  const [display, setDisplay] = useState(0);
  const parsed = parseTarget(value);

  useEffect(() => {
    if (!parsed.hasNumber) return;
    const el = elRef.current;
    if (!el) return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) { setDisplay(parsed.target); return; }

    let raf, start;
    let played = false;
    const ease = (t) => 1 - Math.pow(1 - t, 3);

    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min(1, (ts - start) / duration);
      setDisplay(parsed.target * ease(p));
      if (p < 1) raf = requestAnimationFrame(step);
    };

    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !played) {
            played = true;
            raf = requestAnimationFrame(step);
            obs.disconnect();
          }
        }
      },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => { obs.disconnect(); cancelAnimationFrame(raf); };
  }, [parsed.target, parsed.hasNumber, duration]);

  if (!parsed.hasNumber) {
    return <span ref={elRef} className={className}>{value}</span>;
  }

  const shown = parsed.decimals > 0
    ? display.toFixed(parsed.decimals)
    : Math.floor(display).toString();

  return (
    <span ref={elRef} className={className}>
      {parsed.prefix}{shown}{parsed.suffix}
    </span>
  );
}
